import { ensureArray } from '../coerce-array';

/**
 * `acmeTree` or `acmeTreeNode` controllers
 */
export interface TreeNodeCtrl<TModel = unknown> {
  model: TModel | undefined;
  node: TreeNode<TModel>;
  add(node: TreeNode): void;
}

export interface TreeNodeOptions<TModel = unknown> {
  id: string;
  parent: TreeNode<TModel> | null;
  children: TreeNode<TModel>[];
  value: TreeNodeCtrl<TModel> | null;
}

export class TreeNode<TModel = unknown> {
  id: string | null;
  parent: TreeNode<TModel> | null;
  children: TreeNode<TModel>[];
  value: TreeNodeCtrl<TModel> | null;

  constructor(options: Partial<TreeNodeOptions<TModel>>) {
    this.id = options.id || null;
    this.parent = options.parent || null;
    this.children = options.children ?? [];
    this.value = options.value ?? null;
  }

  isRoot() {
    return this.parent == null;
  }

  isLeaf() {
    return this.children.length === 0;
  }

  add(node: TreeNode<TModel>) {
    this.children.push(node);
  }

  traverse(callbackFn: (node: TreeNode<TModel>) => void) {
    const queue = [this];

    this.internalTraverse(callbackFn, queue);
  }

  traverseChildren(callbackFn: (node: TreeNode<TModel>) => void) {
    const queue = this.children.map((child) => child);

    this.internalTraverse(callbackFn, queue);
  }

  private internalTraverse<T>(callbackFn: (node: TreeNode<T>) => void, queue: TreeNode<T>[]) {
    queue = ensureArray(queue);

    while (queue.length) {
      const node = queue.shift();

      if (node == null) {
        continue;
      }

      if (typeof callbackFn === 'function') {
        callbackFn(node);
      }

      for (const child of node.children) {
        queue.push(child);
      }
    }
  }
}
